/**
 * 원단 견본 사진 (컨셉 §7.7 · 루브릭 R5).
 *
 * 대체 순서: (`small` 이 있으면) 작은 견본 swatch_sm_url 240 → `url`(표시용 견본 swatch_url 400 — 촬영대 틀을 걷어 낸 원단 부분)
 *          → `fallback`(촬영 원본 썸네일 thumb_url, 여럿이면 차례로) → 빈 견본(표면색 + 견본 아이콘).
 *          사진을 못 받아도(서명 만료 · 404 · 차단) 다음 것으로 조용히 넘어간다.
 * `small` + `sizes` — 작은 칸(카드 · 모자이크 · 색 계열 · 배색 · 트레이 · 관리 줄)은 `srcset="{sm} 240w, {swatch} 400w"` 로 칸 크기 × 화면 밀도에 맞춰
 *   브라우저가 고른다(1배 화면 133px 칸 → 240, 2배 화면 → 400). 2026-10-18 서버 swatch-sm(평균 12.6KB — 견본 400 은 평균 47KB).
 *   작은 견본을 못 받으면 견본 400 하나로 한 번 더(srcset 없이), 그래도 못 받으면 썸네일 — sm → 견본 → 썸네일 → 빈 견본.
 * 크기는 부모(정사각 칸 · aspect-ratio)가 정하고, 사진은 그 칸을 덮는다(object-fit: cover) — 견본이 없어도 자리가 그대로라 밀리지 않는다(CLS).
 * 사진은 손대지 않는다 — 필터 · 색 보정 · 덮개 없음. 흰 원단이 배경에 묻히지 않게 칸 안쪽 1px --line 테두리는 CSS(.sw-frame)가 맡는다.
 *
 * 지연 로딩: 기본 loading="lazy" · decoding="async". 첫 화면 모자이크만 eager.
 * `defer` — 브라우저 지연 거리(느린 망 약 2,500px)에 맡기지 않고 near.ts 관찰자가 「화면 가까이」 라고 할 때 사진 주소를 넣는다
 *   (15회차 M33 · Codex 8차 B6). 그 전에는 같은 크기의 옅은 면(.sw-wait)만 — 요청 0.
 * `priority` — fetchpriority(첫 화면 모자이크 high, 나머지 low). React 18 은 이 속성을 몰라 소문자 그대로 넘긴다.
 * 처음부터 막힌 사진(광고 차단 확장의 ERR_BLOCKED_BY_CLIENT 등)은 React 가 화면을 확정하기 전에 오류가 와 onError 가 불리지 않는다 —
 *   확정 직후 `complete && naturalWidth === 0` 이면 다음 후보로(15회차 관찰 ③). 같은 사진의 오류를 두 번 세지 않게 「그 주소가 지금 후보일 때만」 넘긴다.
 *
 * 모의 모드(서버 없음)에서는 사진 주소가 없다 — 목업과 같은 CSS 무늬(mock.ts SWATCH_OF)로 그린다. 실서버 모드는 이 분기에 오지 않는다.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { mediaSrcSet, mediaUrl } from '../api/media';
import { getMode } from '../api/client';
import { useNear } from '../near';
import { Icon } from './Icon';

const PATTERNS: Record<string, React.CSSProperties> = {
  'floral-yellow': {
    background: `
      radial-gradient(circle at 20% 22%, #F2D94E 0 7px, transparent 8px),
      radial-gradient(circle at 20% 22%, #4A7C3A 0 3px, transparent 4px),
      radial-gradient(circle at 70% 62%, #F2D94E 0 7px, transparent 8px),
      radial-gradient(circle at 70% 62%, #4A7C3A 0 3px, transparent 4px),
      #23282F`,
    backgroundSize: '74px 74px, 74px 74px, 74px 74px, 74px 74px, 100% 100%',
  },
  'floral-green': {
    background: `radial-gradient(circle at 25% 25%, #F5E07A 0 8px, transparent 9px), #5C6B3A`,
    backgroundSize: '52px 52px, 100% 100%',
  },
  'floral-big': {
    background: `radial-gradient(circle at 40% 35%, #E8C93F 0 13px, transparent 14px), #3A3F36`,
    backgroundSize: '78px 78px, 100% 100%',
  },
  'floral-pink': {
    background: `radial-gradient(circle at 30% 30%, #F0A8BD 0 9px, transparent 10px), #F6E6EA`,
    backgroundSize: '48px 48px, 100% 100%',
  },
  'dot-amber': {
    background: `radial-gradient(circle, #F2D94E 0 4px, transparent 5px), #7A6A3A`,
    backgroundSize: '24px 24px, 100% 100%',
  },
  'stripe-navy': { background: 'repeating-linear-gradient(90deg,#2F4F7A 0 16px,#E9E4D8 16px 30px)' },
  'stripe-green': { background: 'repeating-linear-gradient(90deg,#3F6B46 0 9px,#587F5C 9px 18px)' },
  'stripe-red': { background: 'repeating-linear-gradient(90deg,#B8422F 0 14px,#C9553C 14px 28px)' },
  'check-brown': {
    background: `
      repeating-linear-gradient(90deg, rgba(60,40,30,.55) 0 22px, transparent 22px 46px),
      repeating-linear-gradient(0deg, rgba(60,40,30,.55) 0 22px, transparent 22px 46px),
      #CBB99A`,
  },
  'geo-blue': {
    background: 'conic-gradient(from 45deg at 50% 50%, #4A90D9 0 25%, #E8EEF7 0 50%, #4A90D9 0 75%, #E8EEF7 0)',
    backgroundSize: '38px 38px',
  },
  'paisley-terra': {
    background: `radial-gradient(ellipse 16px 9px at 40% 40%, #E8955A 0 100%, transparent 0), #8A4A2A`,
    backgroundSize: '58px 58px, 100% 100%',
  },
  'jacquard-beige': {
    background: `
      repeating-linear-gradient(45deg, rgba(255,255,255,.14) 0 4px, transparent 4px 9px),
      repeating-linear-gradient(-45deg, rgba(0,0,0,.10) 0 4px, transparent 4px 9px),
      #D8C6A4`,
  },
  'solid-beige': { background: 'linear-gradient(135deg,#DCC9A8,#CDB894)' },
  'solid-cream': { background: 'linear-gradient(135deg,#F4EFE4,#E8E0CF)' },
  'solid-ivory': { background: 'linear-gradient(135deg,#EFE7D8,#E0D5C0)' },
  'solid-charcoal': { background: 'linear-gradient(135deg,#4A4E54,#33373C)' },
};

interface Props {
  fabricId: string;
  /** 먼저 쓸 사진 — 보통 견본(swatch_url). 상세 「촬영 원본」 처럼 원본을, 넓은 칸이면 가로 띠(banner_url)를 먼저 줄 수도 있다 */
  url?: string | null;
  /** url 이 없거나 못 받으면 쓸 사진 — 보통 촬영 원본 썸네일(thumb_url). 여럿이면 차례로(띠 → 견본 → 썸네일) */
  fallback?: string | null | Array<string | null | undefined>;
  className?: string;
  alt?: string;
  /** SEEDS 에 없는 원단(예: 검수 큐 모의 데이터)에 패턴을 직접 지정 */
  patternKey?: string;
  /** 상세 대표 이미지 — 서버 파생 변형(thumb 400 · preview 1600)을 폭에 맞춰 고른다 (리뷰 5회차 m14). url 에만 붙는다 */
  srcSet?: string;
  /** srcSet · small 을 쓸 때 칸 크기(필수 — 없으면 small 을 쓰지 않는다) */
  sizes?: string;
  /** 작은 견본(swatch_sm_url, 240) — url(견본 400)과 함께 srcset 으로 */
  small?: string | null;
  /** 첫 화면에 보이는 사진(홈 모자이크 · 상세 대표) — 바로 받는다 */
  eager?: boolean;
  /** 화면 가까이 올 때까지 사진 주소를 넣지 않는다(near.ts — NearScope 안에서만 미룬다) */
  defer?: boolean;
  /** 받는 순서 힌트(fetchpriority) */
  priority?: 'high' | 'low';
  /** 빈 견본 아이콘 크기 */
  iconSize?: number;
}

export default function Swatch({ fabricId, url, fallback, className, alt, patternKey, srcSet, sizes, small, eager, defer, priority, iconSize = 24 }: Props) {
  // 리뷰 5회차 m25: 내장 샘플(mock.ts 29KB)은 모의 모드에서만 지연 로딩한다.
  const [seedKey, setSeedKey] = useState<string | undefined>();
  /** 받을 차례 — src 와(있으면) srcset. kind: 'fallback' 은 url 이 아닌 대체 사진(썸네일 등) */
  type Cand = { src: string; set?: string; sizes?: string; fb?: boolean };
  const cands: Cand[] = [];
  const add = (c: Cand) => { if (!cands.some((x) => x.src === c.src && x.set === c.set)) cands.push(c); };
  if (url) {
    if (srcSet && sizes) add({ src: url, set: srcSet, sizes });
    else if (small && sizes && small !== url) add({ src: url, set: `${small} 240w, ${url} 400w`, sizes });
    add({ src: url });
  } else if (small) add({ src: small });
  for (const u of Array.isArray(fallback) ? fallback : [fallback]) if (u && !cands.some((x) => x.src === u)) add({ src: u, fb: true });
  const ckey = cands.map((c) => `${c.src}|${c.set ?? ''}`).join(' ');
  /**
   * 못 받은 차례 수 — 0: 첫 차례, 1: 다음 … 다 쓰면 빈 견본. 주소가 바뀌면(새 서명) 처음부터.
   * 주소 묶음(key)과 함께 들고 있어, 바뀐 첫 그림에서 앞 묶음의 실패 수로 다른 사진을 한 번 받는 일이 없다
   */
  const [fail, setFail] = useState({ key: ckey, n: 0 });
  const failed = fail.key === ckey ? fail.n : 0;
  const cur = cands[failed];
  const src = cur?.src;
  /**
   * 그 차례가 지금 차례일 때만 넘긴다 — onError 와 확정 직후 검사가 같은 실패를 두 번 세지 않게.
   * srcset 차례에서 브라우저가 고른 것이 url 자체였으면(고밀도 화면에서 400) 「url 하나로 한 번 더」 는 건너뛴다(같은 실패를 되풀이하지 않게)
   */
  const skip = (i: number, picked?: string) => setFail((f) => {
    const n = f.key === ckey ? f.n : 0;
    if (n !== i) return f;
    const c = cands[i];
    const next = cands[i + 1];
    const sameAgain = !!c?.set && !!picked && !!next && !next.set && next.src === c.src
      && picked === new URL(mediaUrl(c.src) ?? '', window.location.href).href;
    return { key: ckey, n: i + (sameAgain ? 2 : 1) };
  });
  const [nearRef, near] = useNear<HTMLElement>(!!defer && !!src, true);
  const img = useRef<HTMLImageElement>(null);
  useLayoutEffect(() => {
    const im = img.current;
    // 확정 전에 이미 실패한 사진(오류 이벤트를 놓침) — 받는 중이면 complete 가 false 라 건드리지 않는다
    if (im && src && im.complete && im.naturalWidth === 0 && im.currentSrc) skip(failed, im.currentSrc);
  });   // eslint-disable-line react-hooks/exhaustive-deps
  const mock = getMode() === 'mock';
  useEffect(() => {
    if (cands.length || patternKey || !mock) return;
    let alive = true;
    void import('../api/mock').then((m) => { if (alive) setSeedKey(m.SWATCH_OF.get(fabricId)); });
    return () => { alive = false; };
  }, [cands.length, patternKey, fabricId, mock]);

  const label = alt ?? fabricId;
  if (cur && !near) {
    // 아직 화면에서 멀다 — 같은 자리 · 같은 크기의 옅은 면(사진 요청 없음)
    return <span ref={nearRef} className={className ? `${className} sw-wait` : 'sw-wait'} aria-hidden="true" />;
  }
  if (cur) {
    const at = failed;
    return (
      <img ref={img} src={mediaUrl(cur.src)} srcSet={cur.set ? mediaSrcSet(cur.set) : undefined} sizes={cur.set ? cur.sizes : undefined}
           alt={label} className={className} width={480} height={480}
           loading={eager ? 'eager' : 'lazy'} decoding="async"
           // 대체 사진(썸네일 등)인지 — 화면이 대체 사진에만 다른 맞춤(배색 견본의 썸네일 가운데 키우기)을 줄 때
           data-kind={cur.fb ? 'fallback' : undefined}
           {...(priority ? { fetchpriority: priority } as Record<string, string> : {})}
           onError={(e) => skip(at, e.currentTarget.currentSrc)} />
    );
  }
  const key = patternKey ?? seedKey;
  const pat = key ? PATTERNS[key] : undefined;
  // alt="" 는 장식(이름은 둘레 링크가 말한다) — 대체 그림도 낭독하지 않는다
  const a11y = alt === '' ? { 'aria-hidden': true as const } : { role: 'img', 'aria-label': pat ? label : `${label} — 사진 없음` };
  if (pat) return <div className={className} style={pat} {...a11y} />;
  // 빈 견본 — 사진이 없거나 모두 못 받았다. 지어낸 그림 대신 표면색 + 견본 아이콘(자리 크기는 그대로)
  return (
    <div className={className ? `${className} sw-empty` : 'sw-empty'} {...a11y}>
      <Icon name="swatch" size={iconSize} />
    </div>
  );
}
